import { useToggle } from 'ahooks';
import Avo from 'analytics/Avo';
import type { PricingList } from 'api/types/pricing.types';
import { ReactComponent as MultiBagIcon } from 'assets/icons/multi-bags-icon.svg';
import CleanPressImg1 from 'assets/images/pricing_images/pricing_cp_1.webp';
import CleanPressImg2 from 'assets/images/pricing_images/pricing_cp_2.webp';
import HomeCareImg1 from 'assets/images/pricing_images/pricing_hc_1.webp';
import HomeCareImg2 from 'assets/images/pricing_images/pricing_hc_2.webp';
import PressOnlyImg1 from 'assets/images/pricing_images/pricing_po_1.webp';
import PressOnlyImg2 from 'assets/images/pricing_images/pricing_po_2.webp';
import WashFoldImg1 from 'assets/images/pricing_images/pricing_wf_1.webp';
import WashFoldImg2 from 'assets/images/pricing_images/pricing_wf_2.webp';
import GettingBagBottomsheet from 'components/organisms/BottomSheets/GettingBagBottomSheet';
import useImagePreloader from 'hooks/useImagePreloader';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { usePricingUIStore } from 'store/pricing.ui.store';

import AddressModule from './address-module';
import { BasketContextProvider } from './basket-bottomsheet/provider/BasketSheetContextProvider';
import { BagInfoContainer, LearnMoreBtn, Title } from './pricing.styles';
import type { PricingPageProps } from './pricing.types';
import ServiceDetails from './serviceDetails';
import ServicesStickyHeader from './services-sticky-header';

function PricingReaction({ isBottomSheet, serviceType }: PricingPageProps) {
  const setPricingType = usePricingUIStore.use.setPricingType();
  const pricingType = usePricingUIStore.use.pricingType();
  const { type } = useParams<{ type: PricingList.ServiceTypes }>();
  useEffect(() => {
    setPricingType(isBottomSheet ? serviceType : type);

    return () => {
      // Reset pricing type when component unmounts
      setPricingType(undefined);
    };
  }, [serviceType, type]);

  useEffect(() => {
    if (pricingType) {
      Avo.viewedPricingPage({
        service: pricingType,
        isBottomSheet: Boolean(isBottomSheet),
      });
      setPricingType(pricingType);
    }
  }, [isBottomSheet, pricingType]);

  useImagePreloader([HomeCareImg1, HomeCareImg2, PressOnlyImg1, PressOnlyImg2, CleanPressImg1, CleanPressImg2, WashFoldImg1, WashFoldImg2]);

  return null;
}

function Pricing({ isBottomSheet, onClose, serviceType = 'CLEAN_PRESS' }: PricingPageProps): JSX.Element {
  const [showGettingBagBottomsheet, { setRight, setLeft }] = useToggle(false);
  const { t } = useTranslation();

  const handleLearnMore = useCallback(() => {
    setRight();
    Avo.learnMoreOnboarding();
  }, [setRight]);

  return (
    <BasketContextProvider>
      <ServicesStickyHeader onClose={onClose} isBottomSheet={isBottomSheet} />
      <BagInfoContainer>
        <Title>
          <MultiBagIcon /> {t('pricingPage.dont_have_bags')}
        </Title>
        <LearnMoreBtn onClick={handleLearnMore}>{t('selectServicesPage.learnMore')}</LearnMoreBtn>
      </BagInfoContainer>
      <ServiceDetails hasNoHorizontalPadding={isBottomSheet} />
      <AddressModule onClose={onClose} isBottomSheet={isBottomSheet} />
      <GettingBagBottomsheet show={showGettingBagBottomsheet} onClose={setLeft} />
      <PricingReaction isBottomSheet={isBottomSheet} serviceType={serviceType} />
    </BasketContextProvider>
  );
}

export default Pricing;
